import { Descriptions, Divider, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
import { type MyInfoModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/MyInfoModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import MyInfoModuleActions from './MyInfoModuleActions';
import MyInfoSettingsEmptyList from './MyInfoSettingsEmptyList';
import MyInfoSettingsTable from './MyInfoSettingsTable';

export type MyInfoModulePageProps = { module: MyInfoModuleWithPermissionsSpecsFragment };

const MyInfoModulePage = ({ module }: MyInfoModulePageProps) => {
    const { t } = useTranslation(['myInfoModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('myInfoModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('myInfoModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<MyInfoModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('myInfoModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            <Divider>{t('myInfoModuleDetails:dividers.myInfoSettings')}</Divider>
            {module.myInfoSettings.length === 0 ? (
                <MyInfoSettingsEmptyList module={module} permissions={permissions} />
            ) : (
                <MyInfoSettingsTable module={module} />
            )}
        </ConsolePageWithHeader>
    );
};

export default MyInfoModulePage;
